
declare global {
  interface Window {
    fbq: (
      action: string,
      event: string,
      params?: Record<string, unknown>
    ) => void;
    _fbq: unknown;
  }
}

export interface ProductEventParams {
  content_name?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
  content_category?: string;
  num_items?: number;
}

const isFbqAvailable = (): boolean =>
  typeof window !== 'undefined' && typeof window.fbq === 'function';

/**
 * Track a PageView event
 */
export const trackPageView = (): void => {
  if (!isFbqAvailable()) return;
  window.fbq('track', 'PageView');
};

/**
 * Track a ViewContent event (product page view)
 */
export const trackViewContent = (params: ProductEventParams): void => {
  if (!isFbqAvailable()) return;
  window.fbq('track', 'ViewContent', {
    content_name: params.content_name,
    content_ids: params.content_ids ?? [],
    content_type: params.content_type ?? 'product',
    value: params.value,
    currency: params.currency ?? 'EUR',
    content_category: params.content_category,
  });
};

/**
 * Track an AddToCart event
 */
export const trackAddToCart = (params: ProductEventParams): void => {
  if (!isFbqAvailable()) return;
  window.fbq('track', 'AddToCart', {
    content_name: params.content_name,
    content_ids: params.content_ids ?? [],
    content_type: params.content_type ?? 'product',
    value: params.value,
    currency: params.currency ?? 'EUR',
    num_items: params.num_items ?? 1,
  });
};

/**
 * Track an InitiateCheckout event
 */
export const trackInitiateCheckout = (params: ProductEventParams): void => {
  if (!isFbqAvailable()) return;
  window.fbq('track', 'InitiateCheckout', {
    content_name: params.content_name,
    content_ids: params.content_ids ?? [],
    content_type: params.content_type ?? 'product',
    value: params.value,
    currency: params.currency ?? 'EUR',
    num_items: params.num_items ?? 1,
  });
};

/**
 * Track a Lead event (form submission)
 */
export const trackLead = (
  params?: { content_name?: string; value?: number; currency?: string }
): void => {
  if (!isFbqAvailable()) return;
  window.fbq('track', 'Lead', {
    content_name: params?.content_name,
    value: params?.value,
    currency: params?.currency ?? 'EUR',
  });
};

/**
 * Track a Contact event (click on email/phone)
 */
export const trackContact = (): void => {
  if (!isFbqAvailable()) return;
  window.fbq('track', 'Contact');
};

/**
 * Track a Search event
 */
export const trackSearch = (searchString?: string): void => {
  if (!isFbqAvailable()) return;
  window.fbq('track', 'Search', {
    search_string: searchString ?? '',
  });
};
